import { CmsProduct } from '../../../../types/cmsProduct';
import { ComponentProductDetails } from '../../../../types/cmsTypes';
import { groupProductAttributes } from '../../../../utils/helpers';
import { getChooseOneOffers } from '../../../../utils/helpers/offers';
import { getPolicyDocuments } from '../../../../utils/tiAttributes';
import {
  resolveTiEligibilityEntries,
  TiEligibilityRowConfig,
} from '../../Sections/Eligibility/tiEligibility';

// Whether a section has anything to show. Each builder decides which sections its vertical has and
// uses these to skip the empty ones, so the sticky nav never links to a missing section.

export const isTiEligibilitySectionVisible = (
  product?: CmsProduct,
  rows?: TiEligibilityRowConfig[],
): boolean => resolveTiEligibilityEntries(product, rows).length > 0;

export const isPolicyDocumentsSectionVisible = (
  product?: CmsProduct,
): boolean => {
  const { policyWording, brochure } = getPolicyDocuments(product);
  return Boolean(policyWording || brochure);
};

/** True when the product has any exclusive / bundle / welcome gift — the Offers section's content. */
export const hasProductOffers = (product?: CmsProduct): boolean => {
  const exclusiveGifts = getChooseOneOffers(product ?? {}).flatMap(
    (offer) => offer.list_of_gifts ?? [],
  );
  const welcomeGifts = (product?.welcome_offers ?? []).flatMap(
    (offer) => offer.list_of_gifts ?? [],
  );
  return exclusiveGifts.length + welcomeGifts.length > 0;
};

/** True when the product carries the "About this product" long description. */
export const hasLongDescription = (product?: CmsProduct): boolean =>
  Boolean(product?.long_description?.trim());

export const isHighlightsSectionVisible = (product?: CmsProduct): boolean => {
  if (!product) return false;
  const quickFacts = product.attributes?.filter(
    (attribute) =>
      attribute.__typename === 'ComponentAttributesAttrKeyValue' &&
      attribute.label === 'Quick Facts',
  );
  return Boolean(product.description) || Boolean(quickFacts);
};

export const isBenefitsSectionVisible = (
  props: Pick<ComponentProductDetails, 'product' | 'benefitCategorySelector'>,
): boolean => {
  const { product, benefitCategorySelector } = props;
  const { benefitsAirmiles, benefitsRewardsPoints, benefitsCashback } =
    groupProductAttributes(product);

  const showCashback =
    !!benefitsCashback?.length &&
    !!benefitCategorySelector?.includes('CASHBACK');
  const showAirmiles =
    !!benefitsAirmiles?.length &&
    !!benefitCategorySelector?.includes('AIRMILES');
  const showRewardsPoints =
    !!benefitsRewardsPoints?.length &&
    !!benefitCategorySelector?.includes('REWARDS_POINTS');

  return showCashback || showAirmiles || showRewardsPoints;
};

export const isFeesSectionVisible = (product?: CmsProduct): boolean => {
  const { primaryFees, fullFeeSchedule, installmentFees } =
    groupProductAttributes(product);
  return Boolean(
    primaryFees?.length || fullFeeSchedule?.length || installmentFees?.length,
  );
};

export const isEligibilitySectionVisible = (product?: CmsProduct): boolean => {
  const { eligibility, requiredDocuments } = groupProductAttributes(product);
  return eligibility.length > 0 || requiredDocuments.length > 0;
};
